import { BrowserModule } from "@angular/platform-browser";
import { ErrorHandler, NgModule, LOCALE_ID } from "@angular/core";
import { IonicApp, IonicErrorHandler, IonicModule } from "ionic-angular";
import {
  HttpClientModule,
  HTTP_INTERCEPTORS,
  HttpClient
} from "@angular/common/http";

import { SocketIoModule, SocketIoConfig } from "ng-socket-io";

import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { StoreModule } from "@ngrx/store";
import { EffectsModule } from "@ngrx/effects";

import lcoaleKo from "@angular/common/locales/ko";
import { registerLocaleData } from "@angular/common";

import { IonicStorageModule } from "@ionic/storage";
import { SplashScreen } from "@ionic-native/splash-screen";
import { StatusBar } from "@ionic-native/status-bar";

import { AppVersion } from "@ionic-native/app-version";
import { InAppBrowser } from "@ionic-native/in-app-browser";
import { Camera } from "@ionic-native/camera";
import { FileTransfer } from "@ionic-native/file-transfer";
import { File } from "@ionic-native/file";
import { FCM } from "@ionic-native/fcm";
import { Badge } from "@ionic-native/badge";
import { Media } from "@ionic-native/media";
import { MediaCapture } from "@ionic-native/media-capture";
import { Keyboard } from "@ionic-native/keyboard";
import { Facebook } from "@ionic-native/facebook";
import { DatePicker } from "@ionic-native/date-picker";
import { VideoEditor } from "@ionic-native/video-editor";
import { Network } from "@ionic-native/network";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { Globalization } from "@ionic-native/globalization";
import { AndroidPermissions } from "@ionic-native/android-permissions";
import { Vibration } from "@ionic-native/vibration";
import { LineLogin } from "@ionic-native/line-login";
import { EmailComposer } from "@ionic-native/email-composer";
import { Clipboard } from "@ionic-native/clipboard";
// import { Diagnostic } from '@ionic-native/diagnostic';
import { PhotoLibrary } from "@ionic-native/photo-library";

import "rxjs/add/operator/map";
import "rxjs/add/operator/mergeMap";
import "rxjs/add/operator/catch";
import "rxjs/add/operator/do";
import "rxjs/add/operator/takeWhile";
import "rxjs/add/operator/filter";
import "rxjs/add/operator/switchMap";
import "rxjs/add/operator/take";
import "rxjs/add/operator/withLatestFrom";
import "rxjs/add/operator/debounceTime";
import "rxjs/add/operator/distinctUntilChanged";
import "rxjs/add/observable/fromPromise";
import "rxjs/add/observable/of";
import "rxjs/add/observable/throw";
import "rxjs/add/observable/combineLatest";

import { MyApp } from "./app.component";
import { ENV } from "../env/env";

import { AuthInterceptor } from "../services/auth.interceptor";
import { AdminService } from "../services/admin.service";
import { AlarmService } from "../services/alarm.service";
import { AlertService } from "../services/alert.service";
import { AuthService } from "../services/auth.service";
import { LoadingService } from "../services/loading.service";
import { PlatformService } from "../services/platform.service";
import { SystemService } from "../services/system.service";
import { UserService } from "../services/user.service";
import { VerifyService } from "../services/verify.service";
import { RealtimeService } from "../services/realtime.service";

import { reducers, effects } from "./app.reducer";
import { ImageService } from "../services/image.service";
import { VideoService } from "../services/video.service";
import { AudioService } from "../services/audio.service";

import { KakaoService } from "../services-sns/kakao.service";
import { FacebookService } from "../services-sns/facebook.service";
import { DonatorService } from "../services/donator.service";
import { CelebService } from "../services/celeb.service";
import { MessageService } from "../services/message.service";
import { CategoryService } from "../services/category.service";
import { ProposeService } from "../services/propose.service";
import { CommentService } from "../services/comment.service";
import { ReportService } from "../services/report.service";
import { PostService } from "../services/post.service";
import { TransService } from "../services/trans.service";
import { ManagerService } from "../services/manager.service";
import { PurchaseService } from "../services/purchase.service";
import { LineService } from "../services-sns/line.service";
import { FormsModule } from "@angular/forms";
import { Video2Service } from "../services/video2.service";
import { NotificationService } from "../services/notificatin.service";
import { PayLogService } from "../services/pay-log.service";
import { ChannelCategoryService } from "../services/channel-category.service";
import { ChannelService } from "../services/channel.service";
import { WatchService } from "../services/watch.service";

const config: SocketIoConfig = {
  url: ENV.SERVER_URL,
  options: {
    path: "/celeb/socket.io"
  }
};

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

registerLocaleData(lcoaleKo);

@NgModule({
  declarations: [MyApp],
  imports: [
    BrowserModule,
    FormsModule,
    HttpClientModule,
    IonicModule.forRoot(MyApp, {
      mode: "md"
    }),
    SocketIoModule.forRoot(config),
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot(effects),
    IonicStorageModule.forRoot(),
    !ENV.PRODUCTION ? StoreDevtoolsModule.instrument() : [],
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    })
  ],
  bootstrap: [IonicApp],
  entryComponents: [MyApp],
  providers: [
    StatusBar,
    SplashScreen,
    Keyboard,
    AppVersion,
    InAppBrowser,
    Camera,
    FileTransfer,
    File,
    FCM,
    Badge,
    Media,
    MediaCapture,
    Facebook,
    DatePicker,
    VideoEditor,
    Network,
    Globalization,
    AndroidPermissions,
    Vibration,
    LineLogin,
    EmailComposer,
    Clipboard,
    PhotoLibrary /*Diagnostic,*/,
    { provide: ErrorHandler, useClass: IonicErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: "en" },
    AdminService,
    AlarmService,
    AlertService,
    AuthService,
    LoadingService,
    CategoryService,
    PlatformService,
    SystemService,
    UserService,
    VerifyService,
    RealtimeService,
    ImageService,
    VideoService,
    Video2Service,
    AudioService,
    MessageService,
    CelebService,
    DonatorService,
    ProposeService,
    CommentService,
    ReportService,
    NotificationService,
    PayLogService,
    KakaoService,
    FacebookService,
    PostService,
    TransService,
    ManagerService,
    PurchaseService,
    LineService,
    ChannelCategoryService,
    ChannelService,
    WatchService
  ]
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts